import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import { Activity, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';

export default function AiHealthCard() {
  const { t } = useTranslation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const response = await api.get('/health/ai', { params: refresh ? { refresh: 1 } : {} });
      setHealth(response.data.data);
    } catch (error) {
      console.error('Failed to fetch AI health:', error);
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const healthy = health?.healthy;
  const autopilot = health?.autopilot;
  const autopilotBlocked = autopilot && (!autopilot.globally_enabled || autopilot.accounts_enabled === 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">{t('ai_health.title')}</h2>
        </div>
        <button
          type="button"
          onClick={() => fetchHealth(true)}
          disabled={loading}
          className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          {t('ai_health.recheck')}
        </button>
      </div>

      <div className="px-6 py-4 space-y-3">
        {!health && !loading && (
          <p className="text-sm text-gray-500">{t('ai_health.load_failed')}</p>
        )}

        {health && (
          <>
            <div className="flex items-start gap-3">
              {healthy
                ? <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                : <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />}
              <div>
                <p className={cn('text-sm font-medium', healthy ? 'text-green-700' : 'text-red-700')}>
                  {t(`ai_health.status.${health.status}`, { defaultValue: health.status })}
                </p>
                <p className="text-sm text-gray-600">
                  {t(`ai_health.detail.${health.status}`, { defaultValue: health.summary })}
                </p>
              </div>
            </div>

            {!healthy && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {t('ai_health.fallback_warning')}
                {health.api_message && (
                  <p className="mt-1 font-mono text-xs break-all">{health.api_message}</p>
                )}
              </div>
            )}

            {autopilotBlocked && (
              <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-700">
                {autopilot.globally_enabled
                  ? t('ai_health.no_accounts_enabled')
                  : t('ai_health.globally_paused')}
              </div>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm pt-1">
              <Detail label={t('ai_health.model')} value={health.model} />
              <Detail label={t('ai_health.api_key')} value={health.api_key_hint || t('ai_health.not_set')} />
              <Detail
                label={t('ai_health.autopilot')}
                value={autopilot?.globally_enabled ? t('ai_health.on') : t('ai_health.off')}
              />
              <Detail
                label={t('ai_health.accounts_enabled')}
                value={autopilot ? `${autopilot.accounts_enabled}/${autopilot.accounts_total}` : '-'}
              />
            </dl>
          </>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 truncate">{value}</dd>
    </div>
  );
}
